/**
 * Builds the REST API base URL for the current account, used by the in-app
 * feature guides to render copy-pasteable `curl` examples.
 *
 * Shape: `{origin}/api/v1/accounts/{accountId}`
 *
 * Kept as a pure function (no Vue/Vuex deps) so it is trivially unit-testable
 * and reusable by both the Reminders and Pipeline guides.
 *
 * @param {string} origin - Usually `window.location.origin`.
 * @param {number|string} accountId - The current account id.
 * @returns {string} The API base URL, without a trailing slash.
 */
export const buildApiBase = (origin, accountId) => {
  const normalizedOrigin = String(origin ?? '').replace(/\/+$/, '');
  const normalizedAccountId = String(accountId ?? '').trim();

  return `${normalizedOrigin}/api/v1/accounts/${normalizedAccountId}`;
};

/**
 * Interpolates the `{base}` placeholder used in the i18n API snippets with the
 * real, account-scoped API base URL.
 *
 * @param {string} template - A string that may contain the `{base}` token.
 * @param {string} base - The resolved API base URL (see {@link buildApiBase}).
 * @returns {string} The interpolated string.
 */
export const withApiBase = (template, base) =>
  String(template ?? '').replaceAll('{base}', base);
